(() => {
  'use strict';
  const template = document.getElementById('sac-cart-recovery-template');
  if (!(template instanceof HTMLTemplateElement) || template.dataset.bound === 'true') return;
  template.dataset.bound = 'true';
  const cookieName = 'sac_cart_recovery_seen';
  const cardSelector = '[data-sac-cart-recovery]';
  const upcartCardSelector = '[data-sac-cart-recovery="upcart"]';
  const footerSelector = '.upcart-footer';
  const watchSelector = '#upCart, #UpcartPopup, cart-drawer, .cart__summary, .upcart-footer, [data-sac-cart-recovery]';
  const attributes = ['disabled', 'aria-disabled', 'aria-hidden', 'hidden', 'inert', 'class', 'style'];
  const staged = new WeakSet();
  const dismissedEvents = new WeakSet();
  const observed = new Set();
  let shadowRoot, shadowObserver, upcartHost, active, missingTimer, missingExpired = false;
  let scheduled = false, suspended = false, blocked = false, probeTimer, probeRemaining = 0, channel;

  function wasSeen() {
    try { return document.cookie.split(';').some(part => part.trim().startsWith(cookieName + '=')); }
    catch { blocked = true; return true; }
  }
  function rememberSeen() {
    try {
      document.cookie = cookieName + '=1; Path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
      if (wasSeen()) return true;
    } catch { /* An optional invitation stays hidden when cookies are blocked. */ }
    blocked = true;
    return false;
  }
  blocked = wasSeen();
  try {
    channel = new BroadcastChannel('sac-cart-recovery');
    channel.addEventListener('message', event => {
      if (event.data === 'dismiss' || (event.data === 'seen' && !active)) {
        blocked = true;
        active = undefined;
        hideAll();
      }
    });
  } catch { /* Cookie checks also run before every reveal and when focus returns. */ }

  function schedule() {
    if (scheduled || suspended) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; if (!suspended) sync(); });
  }
  function parentOf(node) { return node.parentElement || node.getRootNode()?.host || null; }
  function ancestorsVisible(node, ignoreOwnVisibility = false) {
    if (!node?.isConnected) return false;
    for (let current = node; current; current = parentOf(current)) {
      if (current.hidden || current.hasAttribute('inert') || current.getAttribute('aria-hidden') === 'true') return false;
      const style = getComputedStyle(current);
      if (style.display === 'none' || Number(style.opacity) === 0) return false;
      if (!(current === node && ignoreOwnVisibility) && /^(hidden|collapse)$/.test(style.visibility)) return false;
    }
    return true;
  }
  function fixedToViewport(node) {
    if (getComputedStyle(node).position !== 'fixed') return false;
    for (let parent = parentOf(node); parent; parent = parentOf(parent)) {
      const style = getComputedStyle(parent);
      if (style.transform !== 'none' || style.perspective !== 'none' || style.filter !== 'none' || /paint|layout|strict|content/.test(style.contain) || /transform|perspective|filter/.test(style.willChange)) return false;
    }
    return true;
  }
  function visibleArea(node, ignoreOwnVisibility = false) {
    if (!ancestorsVisible(node, ignoreOwnVisibility)) return false;
    const rect = node.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    let left = Math.max(0, rect.left), top = Math.max(0, rect.top);
    let right = Math.min(innerWidth, rect.right), bottom = Math.min(innerHeight, rect.bottom);
    for (let child = node, parent = parentOf(node); parent; child = parent, parent = parentOf(parent)) {
      // A viewport-fixed panel escapes overflow on its structural ancestors.
      // Keep clipping within the panel, and within transformed containing blocks.
      if (fixedToViewport(child)) break;
      const style = getComputedStyle(parent), clip = parent.getBoundingClientRect();
      if (/(hidden|clip|auto|scroll)/.test(style.overflowX)) { left = Math.max(left, clip.left); right = Math.min(right, clip.right); }
      if (/(hidden|clip|auto|scroll)/.test(style.overflowY)) { top = Math.max(top, clip.top); bottom = Math.min(bottom, clip.bottom); }
    }
    return right - left >= Math.min(80, rect.width) && bottom - top >= Math.min(24, rect.height);
  }
  function surfaceFor(card) {
    const root = card.getRootNode();
    if (card.dataset.sacCartRecovery === 'upcart') {
      const footer = card.closest(footerSelector);
      // Upcart's open-state wrapper can have zero height at the page footer.
      // Its fixed cart panel supplies geometry; the wrapper supplies state.
      return {key: 'upcart', card, root, container: root.querySelector('#CartPopup, .upcart-cart') || root.querySelector('#UpcartPopup') || footer?.parentElement || footer};
    }
    const drawer = card.closest('cart-drawer');
    return {key: drawer ? 'drawer' : 'page', card, root, container: drawer || card.closest('.cart__summary') || card.parentElement};
  }
  function isOpen(surface) {
    if (!ancestorsVisible(surface?.container)) return false;
    const upcartState = surface.key === 'upcart' && surface.root.querySelector('#UpcartPopup');
    if (upcartState && !upcartState.classList.contains('upcartPopupShow')) return false;
    return surface.key === 'page' || visibleArea(surface.container);
  }
  function hide(card) {
    if (!card.hidden) card.hidden = true;
    if (staged.has(card)) { staged.delete(card); card.style.removeProperty('visibility'); }
  }
  function show(card) {
    if (staged.has(card)) { staged.delete(card); card.style.removeProperty('visibility'); }
    if (card.hidden) card.hidden = false;
  }
  function cards() {
    return [...document.querySelectorAll(cardSelector), ...(shadowRoot ? shadowRoot.querySelectorAll(cardSelector) : [])];
  }
  function hideAll() { cards().forEach(hide); }
  function finish() {
    active = undefined;
    blocked = true;
    window.clearTimeout(missingTimer);
    missingTimer = undefined;
    hideAll();
  }
  function clearMissing() {
    window.clearTimeout(missingTimer);
    missingTimer = undefined;
    missingExpired = false;
  }
  const intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(schedule, {threshold: [0, 0.1, 0.5, 1]}) : null;
  function observeVisibility(elements) {
    if (!intersection) return;
    const wanted = new Set(elements.filter(Boolean));
    for (const element of observed) if (!wanted.has(element)) { intersection.unobserve(element); observed.delete(element); }
    for (const element of wanted) if (!observed.has(element)) { observed.add(element); intersection.observe(element); }
  }
  function applyFrequency(allCards) {
    const surfaces = allCards.map(surfaceFor);
    observeVisibility(surfaces.flatMap(surface => [surface.container, surface.card]));
    if (blocked) { allCards.forEach(hide); return; }
    if (active) {
      const current = surfaces.find(surface => surface.key === active.key);
      if (current && isOpen(current)) {
        clearMissing();
        active = current;
        for (const card of allCards) card === current.card && card.dataset.sacRecoveryAvailable !== 'false' ? show(card) : hide(card);
        return;
      }
      // A footer may disappear during a React redraw. Keep the current opening
      // while its container is open, and briefly allow replacement of the root.
      if (active.container?.isConnected) {
        if (!isOpen(active)) finish();
        else allCards.forEach(hide);
        return;
      }
      if (missingExpired) { finish(); return; }
      if (!missingTimer) missingTimer = window.setTimeout(() => { missingExpired = true; missingTimer = undefined; schedule(); }, 400);
      allCards.forEach(hide);
      return;
    }
    if (wasSeen()) { finish(); return; }
    // Upcart can cover a native drawer that still reports open underneath it.
    // Select the foreground cart before a native drawer or the full cart page.
    const priority = {upcart: 0, drawer: 1, page: 2};
    const open = surfaces.filter(surface => surface.card.dataset.sacRecoveryAvailable !== 'false' && isOpen(surface)).sort((a, b) => priority[a.key] - priority[b.key]);
    const drawerOpen = open.some(surface => surface.key !== 'page');
    const candidate = open.find(surface => (!drawerOpen || surface.key !== 'page') && visibleArea(surface.card.parentElement));
    for (const card of allCards) if (card !== candidate?.card) hide(card);
    if (!candidate) return;
    const card = candidate.card;
    if (!staged.has(card)) {
      staged.add(card);
      card.style.visibility = 'hidden';
      card.hidden = false;
    }
    if (!visibleArea(card, true) || document.hidden) return;
    // Recheck shared state immediately before committing the visible exposure.
    if (wasSeen() || !rememberSeen()) { finish(); return; }
    active = candidate;
    show(card);
    try { channel?.postMessage('seen'); } catch { /* Cookie remains authoritative. */ }
  }

  function copyStyles(root) {
    const source = document.querySelector('link[rel="stylesheet"][href*="sac-cart-recovery.css"]');
    if (!source?.href) return;
    let link = root.querySelector('link[data-sac-cart-recovery-styles]');
    if (!link) {
      link = document.createElement('link');
      link.id = 'sac-cart-recovery-shadow-styles';
      link.rel = 'stylesheet';
      link.dataset.sacCartRecoveryStyles = '';
      link.href = source.href;
      root.append(link);
    } else if (link.href !== source.href) link.href = source.href;
  }
  function selectRoot() {
    upcartHost = document.getElementById('upCart');
    const next = upcartHost?.shadowRoot;
    if (next !== shadowRoot) {
      shadowObserver?.disconnect();
      shadowRoot?.removeEventListener('scroll', schedule, true);
      shadowRoot?.removeEventListener('transitionend', schedule);
      shadowRoot?.removeEventListener('click', dismissInvitation, true);
      shadowRoot = next;
      if (shadowRoot) {
        shadowObserver = new MutationObserver(schedule);
        shadowObserver.observe(shadowRoot, {childList: true, subtree: true, attributes: true, attributeFilter: attributes});
        shadowRoot.addEventListener('scroll', schedule, true);
        shadowRoot.addEventListener('transitionend', schedule);
        shadowRoot.addEventListener('click', dismissInvitation, true);
      }
    }
    if (shadowRoot) {
      window.clearTimeout(probeTimer);
      probeTimer = undefined;
      probeRemaining = 0;
      copyStyles(shadowRoot);
    }
    return shadowRoot || document;
  }
  function syncUpcart(root) {
    const footer = root.querySelector(footerSelector);
    if (!footer) return;
    const wrapper = footer.querySelector('.upcart-checkout-button-container');
    const checkout = wrapper?.querySelector('.upcart-checkout-button');
    const existing = [...footer.querySelectorAll(upcartCardSelector)];
    let card = existing.shift();
    existing.forEach(duplicate => duplicate.remove());
    const available = checkout && !checkout.disabled && checkout.getAttribute('aria-disabled') !== 'true' && !checkout.hidden && !wrapper.hidden && getComputedStyle(checkout).display !== 'none' && getComputedStyle(wrapper).display !== 'none';
    if (!available) { if (card) { card.dataset.sacRecoveryAvailable = 'false'; hide(card); } return; }
    if (!card && !blocked) {
      card = template.content.querySelector(upcartCardSelector)?.cloneNode(true);
      if (card) card.hidden = true;
    }
    if (!card) return;
    card.dataset.sacRecoveryAvailable = 'true';
    if (card.previousElementSibling !== wrapper) wrapper.after(card);
  }
  function sync() {
    syncUpcart(selectRoot());
    applyFrequency(cards());
  }
  // attachShadow does not emit a document mutation. Probe only during startup
  // and app lifecycle events; stop after 15 seconds or when the root is found.
  function probeForShadowRoot() {
    if (document.getElementById('upCart')?.shadowRoot || probeTimer || blocked) return;
    probeRemaining = 60;
    const probe = () => {
      probeTimer = undefined;
      if (document.getElementById('upCart')?.shadowRoot) schedule();
      else if (--probeRemaining > 0) probeTimer = window.setTimeout(probe, 250);
    };
    probeTimer = window.setTimeout(probe, 250);
  }
  function refresh() { schedule(); probeForShadowRoot(); }
  const discovery = new MutationObserver(records => {
    if (document.getElementById('upCart') !== upcartHost) { refresh(); return; }
    if (records.some(record => record.target instanceof Element && (record.target.matches(watchSelector) || record.target.closest(watchSelector)) || [...record.addedNodes, ...record.removedNodes].some(node => node instanceof Element && (node.matches(watchSelector) || node.querySelector(watchSelector))))) schedule();
  });
  discovery.observe(document.body, {childList: true, subtree: true, attributes: true, attributeFilter: attributes});
  function dismissInvitation(event) {
    if (dismissedEvents.has(event)) return;
    const dismiss = event.composedPath().find(node => node instanceof Element && node.matches('[data-sac-recovery-dismiss]'));
    if (!dismiss) return;
    dismissedEvents.add(event);
    event.preventDefault();
    const surface = surfaceFor(dismiss.closest(cardSelector));
    const checkout = surface.container?.querySelector('.upcart-checkout-button, button[name="checkout"]');
    rememberSeen();
    finish();
    try { channel?.postMessage('dismiss'); } catch { /* Current tab is already dismissed. */ }
    if (checkout && !checkout.disabled) checkout.focus({preventScroll: true});
  }
  document.addEventListener('click', dismissInvitation, true);
  window.addEventListener('pagehide', () => { suspended = true; active = undefined; hideAll(); });
  window.addEventListener('pageshow', event => { suspended = false; if ((event.persisted || !active) && wasSeen()) blocked = true; refresh(); });
  window.addEventListener('focus', schedule);
  window.addEventListener('resize', schedule);
  document.addEventListener('visibilitychange', schedule);
  document.addEventListener('scroll', schedule, true);
  document.addEventListener('transitionend', schedule);
  window.addEventListener('aftersell-upcart:public-events:cart-loaded', refresh);
  window.addEventListener('aftersell-upcart:public-events:cart-updated', refresh);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('on:cart:add', refresh);
  document.addEventListener('on:line-item:change', refresh);
  sync();
  probeForShadowRoot();
})();
